import { useList, useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { ChatRoomType, ChatParticipantStatus, ChatRoomKind, MessageDeliveryStatus } from '@masters-club/types';
import {
  MessageSquare,
  Users,
  Clock,
  Eye,
  Filter,
  User,
  AlertTriangle,
  VolumeX,
  Ban,
  UserX,
  Send,
  Trash2,
  RefreshCw,
  Pin,
  PinOff,
  Wrench,
  MoreHorizontal,
  X,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { DataTable, type Column, type SortState, type BulkAction } from '@/components/tables';
import { resolveApiUrl } from '@/providers/api-url';
import { MastersClubIcon } from '@/components/icons/masters-club-icon';
import { cn } from '@/lib/utils';
import { AdminMessageBubble } from '@/components/chat/admin-message-bubble';
import {
  type ChatRoomWithStats,
  type ChatMessageWithSender,
  type ChatParticipant,
  type ChatModerationAction,
  CHAT_ROOM_TYPE_LABELS,
  CHAT_ROOM_TYPE_COLORS,
  PARTICIPANT_STATUS_LABELS,
  PARTICIPANT_STATUS_COLORS,
} from './chat-types';

function formatDate(dateString: string | null) {
  if (!dateString) {return '—';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getParticipantName(participant: ChatParticipant): string {
  const name = [participant.firstName, participant.lastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (participant.username) {return `@${participant.username}`;}
  return `${participant.type === 'admin' ? 'Админ' : 'Пользователь'} #${participant.id}`;
}

function getChatName(room: ChatRoomWithStats): string {
  if (room.name) {return room.name;}
  if (room.kind === ChatRoomKind.LIVESTREAM) {return `Чат трансляции #${room.id}`;}
  if (room.type === ChatRoomType.DIRECT && room.participants.length === 2) {
    return room.participants.map(p => getParticipantName(p)).join(' ↔ ');
  }
  if (room.participants.length > 0) {
    const names = room.participants.slice(0, 3).map(p => getParticipantName(p));
    if (room.participants.length > 3) {
      names.push(`+${room.participants.length - 3}`);
    }
    return names.join(', ');
  }
  return `Чат #${room.id}`;
}

function getChatTypeLabel(room: ChatRoomWithStats): string {
  if (room.kind === ChatRoomKind.LIVESTREAM) {return 'Трансляция';}
  return CHAT_ROOM_TYPE_LABELS[room.type];
}

function canAdminSendMessage(room: ChatRoomWithStats): boolean {
  return room.type === ChatRoomType.SUPPORT || room.kind === ChatRoomKind.LIVESTREAM;
}

function isChatMessageTarget(
  target: ChatParticipant | ChatMessageWithSender | null,
): target is ChatMessageWithSender {
  return target !== null && 'content' in target;
}

function isParticipantTarget(
  target: ChatParticipant | ChatMessageWithSender | null,
): target is ChatParticipant {
  return target !== null && 'isMuted' in target;
}

type ChatModerationActionType =
  | 'mute'
  | 'unmute'
  | 'kick'
  | 'ban'
  | 'unban'
  | 'warn'
  | 'delete'
  | 'deleteOwn'
  | 'edit'
  | 'pin'
  | 'unpin';

function getActionDialogTitle(type: ChatModerationActionType): string {
  switch (type) {
    case 'delete':
      return 'Удалить сообщение';
    case 'deleteOwn':
      return 'Удалить своё сообщение';
    case 'edit':
      return 'Редактировать сообщение';
    case 'pin':
      return 'Закрепить сообщение';
    case 'unpin':
      return 'Снять закрепление';
    case 'mute':
      return 'Замутить пользователя';
    case 'unmute':
      return 'Размутить пользователя';
    case 'kick':
      return 'Выгнать из чата';
    case 'ban':
      return 'Забанить пользователя';
    case 'unban':
      return 'Разбанить пользователя';
    case 'warn':
      return 'Предупредить пользователя';
  }
}

function getActionDialogDescription(type: ChatModerationActionType): string {
  switch (type) {
    case 'delete':
      return 'Сообщение будет помечено как удалённое. Автор получит предупреждение.';
    case 'deleteOwn':
      return 'Сообщение модератора будет помечено как удалённое без предупреждения пользователю.';
    case 'edit':
      return 'Изменение будет видно участникам чата.';
    case 'pin':
      return 'Сообщение будет закреплено вверху чата.';
    case 'unpin':
      return 'Закреплённое сообщение будет снято.';
    case 'mute':
      return 'Пользователь не сможет отправлять сообщения в этом чате.';
    case 'unmute':
      return 'Пользователь снова сможет отправлять сообщения в этом чате.';
    case 'kick':
      return 'Пользователь будет удалён из чата.';
    case 'ban':
      return 'Пользователь будет заблокирован в этом чате.';
    case 'unban':
      return 'Пользователь снова сможет открыть чат.';
    case 'warn':
      return 'Пользователь получит предупреждение.';
  }
}

function actionNeedsReason(type: ChatModerationActionType): boolean {
  return type !== 'pin' &&
    type !== 'unpin' &&
    type !== 'unmute' &&
    type !== 'unban' &&
    type !== 'edit' &&
    type !== 'deleteOwn';
}

function getModerationActionUserName(action: ChatModerationAction): string {
  if (!action.user) {return 'Пользователь удалён';}
  const name = [action.user.firstName, action.user.lastName].filter(Boolean).join(' ');
  return name || action.user.username || `Пользователь #${action.user.id}`;
}

function getModerationActionAdminName(action: ChatModerationAction): string {
  if (!action.admin) {return 'Администратор удалён';}
  return action.admin.username || action.admin.email || `Админ #${action.admin.id}`;
}

function getRevokedByAdminName(action: ChatModerationAction): string {
  if (!action.revokedByAdmin) {return 'Администратор';}
  return action.revokedByAdmin.username || action.revokedByAdmin.email || `Админ #${action.revokedByAdmin.id}`;
}

function formatDurationLabel(minutes: number | null): string {
  if (minutes === null) {return 'бессрочно';}
  if (minutes < 60) {return `${minutes} мин.`;}
  if (minutes < 1440) {return `${Math.round(minutes / 60)} ч.`;}
  return `${Math.round(minutes / 1440)} дн.`;
}

function getModerationActionMeta(action: ChatModerationAction): {
  label: string;
  badge: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: ReactNode;
} {
  switch (action.type) {
    case 'WARNING':
      return { label: 'Варн', badge: 'outline', icon: <AlertTriangle className="h-4 w-4" /> };
    case 'MUTE':
      return { label: 'Мут', badge: 'secondary', icon: <VolumeX className="h-4 w-4" /> };
    case 'KICK':
      return { label: 'Кик', badge: 'default', icon: <UserX className="h-4 w-4" /> };
    case 'BAN':
      return { label: 'Бан', badge: 'destructive', icon: <Ban className="h-4 w-4" /> };
    case 'MESSAGE_DELETED':
      return { label: 'Удаление', badge: 'secondary', icon: <Trash2 className="h-4 w-4" /> };
  }
}

function MessageItem({
  message,
  onEdit,
  onDelete,
  onWarn,
  onMute,
  onPin,
  isDeleting,
  isPinned,
  muteLabel,
  forceActionsOpen = false,
  menuPortalContainer,
}: {
  message: ChatMessageWithSender;
  onEdit?: () => void;
  onDelete: () => void;
  onWarn: () => void;
  onMute?: () => void;
  onPin: () => void;
  isDeleting: boolean;
  isPinned: boolean;
  muteLabel?: string;
  forceActionsOpen?: boolean;
  menuPortalContainer?: HTMLElement | null;
}) {
  const [isInlineMenuOpen, setIsInlineMenuOpen] = useState(false);
  const isAdminSender = message.sender?.type === 'admin';
  const hasClubSubscription = message.sender?.type === 'user' && message.sender.hasActiveSubscription === true;
  const canShowMessageActions = !message.isDeleted;
  const canModerateSender = !message.isSystemMessage && message.sender?.type === 'user';
  const actionsVisibilityClassName = forceActionsOpen
    ? 'opacity-100'
    : 'opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100';

  const badges = (
    <>
      {hasClubSubscription && (
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary" title="Участник клуба">
          <MastersClubIcon className="h-3 w-3" />
        </span>
      )}
      {isAdminSender && (
        <Badge variant="secondary" className="gap-1 text-xs">
          <Wrench className="h-3 w-3" />
          Модератор
        </Badge>
      )}
    </>
  );

  const actions = canShowMessageActions ? (
    <div className={cn('flex items-center gap-1 transition-opacity', actionsVisibilityClassName)}>
      {forceActionsOpen && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onPin}
            title={isPinned ? 'Открепить' : 'Закрепить'}
          >
            {isPinned ? <PinOff /> : <Pin />}
          </Button>
          {isAdminSender && onEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={onEdit}
              title="Редактировать"
            >
              <Pencil />
            </Button>
          )}
          {canModerateSender && (
            <>
              {onMute && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={onMute}
                  title={muteLabel ?? 'Замутить'}
                >
                  <VolumeX />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={onWarn}
                title="Предупредить"
              >
                <AlertTriangle />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                onClick={onDelete}
                disabled={isDeleting}
                title="Удалить"
              >
                <Trash2 />
              </Button>
            </>
          )}
          {isAdminSender && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-destructive hover:text-destructive"
              onClick={onDelete}
              disabled={isDeleting}
              title="Удалить"
            >
              <Trash2 />
            </Button>
          )}
        </>
      )}
      {menuPortalContainer ? (
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={() => setIsInlineMenuOpen((open) => !open)}
          >
            <MoreHorizontal />
            <span className="sr-only">Действия сообщения</span>
          </Button>
          {isInlineMenuOpen && (
            <div className={cn(
              'absolute top-full z-50 mt-1 w-48 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
              isAdminSender ? 'right-0' : 'left-0',
            )}>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => {
                  setIsInlineMenuOpen(false);
                  onPin();
                }}
              >
                {isPinned ? <PinOff /> : <Pin />}
                {isPinned ? 'Открепить' : 'Закрепить'}
              </Button>
              {isAdminSender && onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => {
                    setIsInlineMenuOpen(false);
                    onEdit();
                  }}
                >
                  <Pencil />
                  Редактировать
                </Button>
              )}
              {canModerateSender && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                      setIsInlineMenuOpen(false);
                      onWarn();
                    }}
                  >
                    <AlertTriangle />
                    Предупредить
                  </Button>
                  {onMute && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => {
                        setIsInlineMenuOpen(false);
                        onMute();
                      }}
                    >
                      <VolumeX />
                      {muteLabel ?? 'Замутить'}
                    </Button>
                  )}
                  <Separator className="my-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-destructive hover:text-destructive"
                    onClick={() => {
                      setIsInlineMenuOpen(false);
                      onDelete();
                    }}
                    disabled={isDeleting}
                  >
                    <Trash2 />
                    Удалить
                  </Button>
                </>
              )}
              {isAdminSender && (
                <>
                  <Separator className="my-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-destructive hover:text-destructive"
                    onClick={() => {
                      setIsInlineMenuOpen(false);
                      onDelete();
                    }}
                    disabled={isDeleting}
                  >
                    <Trash2 />
                    Удалить
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-7">
              <MoreHorizontal />
              <span className="sr-only">Действия сообщения</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={onPin}>
              {isPinned ? <PinOff /> : <Pin />}
              {isPinned ? 'Открепить' : 'Закрепить'}
            </DropdownMenuItem>
            {isAdminSender && onEdit && (
              <DropdownMenuItem onClick={onEdit}>
                <Pencil />
                Редактировать
              </DropdownMenuItem>
            )}
            {canModerateSender && (
              <>
                <DropdownMenuItem onClick={onWarn}>
                  <AlertTriangle />
                  Предупредить
                </DropdownMenuItem>
                {onMute && (
                  <DropdownMenuItem onClick={onMute}>
                    <VolumeX />
                    {muteLabel ?? 'Замутить'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 />
                  Удалить
                </DropdownMenuItem>
              </>
            )}
            {isAdminSender && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 />
                  Удалить
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  ) : null;

  return (
    <AdminMessageBubble
      message={{
        ...message,
        status: MessageDeliveryStatus.SENT,
        reactions: [],
        readBy: [],
        isPinned,
      }}
      actions={actions}
      badges={badges}
      contentClassName={message.isDeleted ? 'opacity-60' : undefined}
    />
  );
}

function ModerationActionsList({
  actions,
  isLoading,
}: {
  actions: ChatModerationAction[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
        <AlertTriangle className="mb-2 h-8 w-8" />
        <p className="text-sm">Действий модерации пока нет</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {actions.map((action) => {
        const meta = getModerationActionMeta(action);
        return (
          <div key={action.id} className="rounded-lg border bg-muted/20 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={meta.badge} className="gap-1">
                    {meta.icon}
                    {meta.label}
                  </Badge>
                  <span className="text-sm font-medium">
                    {getModerationActionAdminName(action)} -&gt; {getModerationActionUserName(action)}
                  </span>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {action.reason}
                </p>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {formatDate(action.createdAt)}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {action.type === 'MUTE' && (
                <Badge variant="outline">Срок: {formatDurationLabel(action.duration)}</Badge>
              )}
              {action.relatedMessage && (
                <Badge variant="outline" className="max-w-full truncate">
                  Сообщение #{action.relatedMessage.id}: {action.relatedMessage.content}
                </Badge>
              )}
              {!action.isActive && action.revokedAt && (
                <Badge variant="secondary">
                  Снято: {getRevokedByAdminName(action)}
                </Badge>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ParticipantsPanel({
  participants,
  onMute,
  onKick,
  onBan,
  onUnmute,
  onUnban,
  onWarn,
}: {
  participants: ChatParticipant[];
  onMute: (participant: ChatParticipant) => void;
  onKick: (participant: ChatParticipant) => void;
  onBan: (participant: ChatParticipant) => void;
  onUnmute: (participant: ChatParticipant) => void;
  onUnban: (participant: ChatParticipant) => void;
  onWarn: (participant: ChatParticipant) => void;
}) {
  return (
    <Card className="h-full">
      <CardHeader className="px-3 py-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>Участники</span>
          <Badge variant="secondary" className="h-5 px-1.5 text-xs tabular-nums">
            {participants.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 px-3 pb-3">
        {participants.map((participant) => (
          <div
            key={`${participant.type}-${participant.id}`}
            className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5"
          >
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
                {participant.type === 'admin'
                  ? <Wrench className="h-3.5 w-3.5 text-primary" />
                  : <User className="h-3.5 w-3.5 text-primary" />}
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  {getParticipantName(participant)}
                  {participant.type === 'user' && participant.hasActiveSubscription === true && (
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary" title="Участник клуба">
                      <MastersClubIcon className="h-3 w-3" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Badge
                    variant={PARTICIPANT_STATUS_COLORS[participant.status]}
                    className="text-xs"
                  >
                    {PARTICIPANT_STATUS_LABELS[participant.status]}
                  </Badge>
                  {participant.isMuted && (
                    <Badge variant="secondary" className="text-xs">
                      <VolumeX className="h-3 w-3 mr-1" />
                      Мут
                    </Badge>
                  )}
                  {participant.isBanned && (
                    <Badge variant="destructive" className="text-xs">
                      <Ban className="h-3 w-3 mr-1" />
                      Бан
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {participant.type === 'user' && participant.status === ChatParticipantStatus.ACTIVE && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm">
                    •••
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-yellow-600"
                      onClick={() => onWarn(participant)}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Предупредить
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => participant.isMuted ? onUnmute(participant) : onMute(participant)}
                    >
                      <VolumeX className="h-4 w-4 mr-2" />
                      {participant.isMuted ? 'Размутить' : 'Замутить'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-orange-600"
                      onClick={() => onKick(participant)}
                    >
                      <UserX className="h-4 w-4 mr-2" />
                      Выгнать
                    </Button>
                    <Separator />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-destructive"
                      onClick={() => participant.isBanned ? onUnban(participant) : onBan(participant)}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      {participant.isBanned ? 'Разбанить' : 'Забанить'}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function ChatModerationPanel({
  room,
  className,
  contentHeightClassName = 'h-[calc(100vh-200px)]',
  headerActions,
  showParticipants = true,
  menuPortalContainer,
}: {
  room: ChatRoomWithStats | null;
  className?: string;
  contentHeightClassName?: string;
  headerActions?: ReactNode;
  showParticipants?: boolean;
  menuPortalContainer?: HTMLElement | null;
}) {
  const [actionDialog, setActionDialog] = useState<{
    type: ChatModerationActionType;
    target: ChatParticipant | ChatMessageWithSender | null;
  } | null>(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('60');
  const [editContent, setEditContent] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [olderMessages, setOlderMessages] = useState<ChatMessageWithSender[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [forceMessageActionsOpen, setForceMessageActionsOpen] = useState(false);
  const [activeChatTab, setActiveChatTab] = useState<'messages' | 'actions'>('messages');
  const messageTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingTextareaSelectionRef = useRef<{
    ownerDocument: Document;
    selectionStart: number;
    selectionEnd: number;
  } | null>(null);
  const reasonTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingReasonSelectionRef = useRef<{
    ownerDocument: Document;
    selectionStart: number;
    selectionEnd: number;
  } | null>(null);
  const invalidate = useInvalidate();

  const MESSAGES_PAGE_SIZE = 50;

  const messagesResult = useCustom<{
    data: ChatMessageWithSender[];
    meta: { nextCursor: number | null };
  }>({
    url: `/admin/chat-moderation/rooms/${room?.id}/messages`,
    method: 'get',
    config: {
      query: { limit: MESSAGES_PAGE_SIZE },
    },
    queryOptions: {
      enabled: !!room,
    },
  });

  const messagesData = messagesResult.query.data;
  const refetchMessages = messagesResult.query.refetch;
  const isLoadingMessages = messagesResult.query.isLoading;
  const firstPage: ChatMessageWithSender[] = messagesData?.data?.data ?? [];
  const nextCursor = messagesData?.data?.meta?.nextCursor ?? null;
  const messages: ChatMessageWithSender[] = [...firstPage, ...olderMessages];
  const hasMoreMessages = cursor !== null ? cursor !== 0 : nextCursor !== null;

  const moderationActionsResult = useCustom<{
    data: ChatModerationAction[];
  }>({
    url: `/admin/chat-moderation/rooms/${room?.id}/moderation-actions`,
    method: 'get',
    config: {
      query: { limit: 100 },
    },
    queryOptions: {
      enabled: !!room,
    },
  });
  const moderationActions: ChatModerationAction[] = moderationActionsResult.query.data?.data?.data ?? [];
  const isLoadingModerationActions = moderationActionsResult.query.isLoading;
  const refetchModerationActions = moderationActionsResult.query.refetch;

  const loadMoreMessages = useCallback(async () => {
    if (!room) {return;}
    const currentCursor = cursor ?? nextCursor;
    if (currentCursor === null) {return;}
    setIsLoadingMore(true);
    try {
      const apiUrl = resolveApiUrl();
      const response = await fetch(
        `${apiUrl}/admin/chat-moderation/rooms/${room.id}/messages?cursor=${currentCursor}&limit=${MESSAGES_PAGE_SIZE}`,
        { credentials: 'include' },
      );
      if (!response.ok) {return;}
      const json: {
        data: ChatMessageWithSender[];
        meta: { nextCursor: number | null };
      } = await response.json();
      setOlderMessages((prev) => [...prev, ...json.data]);
      setCursor(json.meta.nextCursor ?? 0);
    } finally {
      setIsLoadingMore(false);
    }
  }, [room, cursor, nextCursor]);

  const resetPagination = useCallback(() => {
    setOlderMessages([]);
    setCursor(null);
  }, []);

  useEffect(() => {
    setOlderMessages([]);
    setCursor(null);
  }, [room?.id]);

  useLayoutEffect(() => {
    const pendingSelection = pendingTextareaSelectionRef.current;
    const textarea = messageTextareaRef.current;
    if (!pendingSelection || !textarea || textarea.ownerDocument !== pendingSelection.ownerDocument) {
      return;
    }

    pendingTextareaSelectionRef.current = null;
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(pendingSelection.selectionStart, pendingSelection.selectionEnd);
  }, [newMessage]);

  useLayoutEffect(() => {
    const pendingSelection = pendingReasonSelectionRef.current;
    const textarea = reasonTextareaRef.current;
    if (!pendingSelection || !textarea || textarea.ownerDocument !== pendingSelection.ownerDocument) {
      return;
    }

    pendingReasonSelectionRef.current = null;
    textarea.focus({ preventScroll: true });
    textarea.setSelectionRange(pendingSelection.selectionStart, pendingSelection.selectionEnd);
  }, [reason, actionDialog?.type]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        setForceMessageActionsOpen(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.altKey) {
        setForceMessageActionsOpen(false);
      }
    };

    const handleWindowBlur = () => {
      setForceMessageActionsOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const deleteMutation = useCustomMutation();
  const muteMutation = useCustomMutation();
  const unmuteMutation = useCustomMutation();
  const kickMutation = useCustomMutation();
  const banMutation = useCustomMutation();
  const unbanMutation = useCustomMutation();
  const warnMutation = useCustomMutation();
  const editMessageMutation = useCustomMutation();
  const sendMessageMutation = useCustomMutation();
  const pinMutation = useCustomMutation();

  const handleSendMessage = useCallback(() => {
    if (!room || !newMessage.trim()) {return;}

    sendMessageMutation.mutate({
      url: `/admin/chat-moderation/rooms/${room.id}/messages`,
      method: 'post',
      values: { content: newMessage.trim() },
    }, {
      onSuccess: () => {
        setNewMessage('');
        resetPagination();
        refetchMessages();
        invalidate({
          resource: 'chat-moderation/rooms',
          invalidates: ['list'],
        });
      },
    });
  }, [room, newMessage, sendMessageMutation, refetchMessages, resetPagination, invalidate]);

  const handleAction = useCallback(() => {
    if (!actionDialog || !room) {return;}

    const onSuccess = () => {
      setActionDialog(null);
      setReason('');
      setEditContent('');
      resetPagination();
      refetchMessages();
      refetchModerationActions();
      invalidate({
        resource: 'chat-moderation/rooms',
        invalidates: ['list'],
      });
    };

    switch (actionDialog.type) {
      case 'delete':
        if (isChatMessageTarget(actionDialog.target)) {
          const msg = actionDialog.target;
          deleteMutation.mutate({
            url: `/admin/chat-moderation/messages/${msg.id}/delete`,
            method: 'post',
            values: { reason },
          }, { onSuccess });
        }
        break;
      case 'deleteOwn':
        if (isChatMessageTarget(actionDialog.target)) {
          const msg = actionDialog.target;
          deleteMutation.mutate({
            url: `/admin/chat-moderation/messages/${msg.id}`,
            method: 'delete',
            values: {},
          }, { onSuccess });
        }
        break;
      case 'edit':
        if (isChatMessageTarget(actionDialog.target)) {
          const msg = actionDialog.target;
          const content = editContent.trim();
          if (!content) {return;}
          editMessageMutation.mutate({
            url: `/admin/chat-moderation/messages/${msg.id}`,
            method: 'put',
            values: { content },
          }, { onSuccess });
        }
        break;
      case 'pin':
        if (isChatMessageTarget(actionDialog.target)) {
          const msg = actionDialog.target;
          pinMutation.mutate({
            url: `/admin/chat-moderation/rooms/${room.id}/pin-message/${msg.id}`,
            method: 'post',
            values: {},
          }, { onSuccess });
        }
        break;
      case 'unpin':
        if (isChatMessageTarget(actionDialog.target)) {
          const msg = actionDialog.target;
          pinMutation.mutate({
            url: `/admin/chat-moderation/rooms/${room.id}/pin-message/${msg.id}`,
            method: 'delete',
            values: {},
          }, { onSuccess });
        }
        break;
      case 'mute':
        if (isParticipantTarget(actionDialog.target)) {
          const participant = actionDialog.target;
          muteMutation.mutate({
            url: `/admin/chat-moderation/users/${participant.id}/mute`,
            method: 'post',
            values: {
              chatRoomId: room.id,
              reason,
              ...(duration === '0' ? {} : { durationMinutes: Number.parseInt(duration, 10) }),
            },
          }, { onSuccess });
        }
        break;
      case 'unmute':
        if (isParticipantTarget(actionDialog.target)) {
          const participant = actionDialog.target;
          unmuteMutation.mutate({
            url: `/admin/chat-moderation/users/${participant.id}/unmute`,
            method: 'post',
            values: { chatRoomId: room.id },
          }, { onSuccess });
        }
        break;
      case 'kick':
        if (isParticipantTarget(actionDialog.target)) {
          const participant = actionDialog.target;
          kickMutation.mutate({
            url: `/admin/chat-moderation/users/${participant.id}/kick`,
            method: 'post',
            values: { chatRoomId: room.id, reason },
          }, { onSuccess });
        }
        break;
      case 'ban':
        if (isParticipantTarget(actionDialog.target)) {
          const participant = actionDialog.target;
          banMutation.mutate({
            url: `/admin/chat-moderation/users/${participant.id}/ban`,
            method: 'post',
            values: { chatRoomId: room.id, reason },
          }, { onSuccess });
        }
        break;
      case 'unban':
        if (isParticipantTarget(actionDialog.target)) {
          const participant = actionDialog.target;
          unbanMutation.mutate({
            url: `/admin/chat-moderation/users/${participant.id}/unban`,
            method: 'post',
            values: { chatRoomId: room.id },
          }, { onSuccess });
        }
        break;
      case 'warn':
        if (actionDialog.target) {
          const userId = isParticipantTarget(actionDialog.target)
            ? actionDialog.target.id
            : actionDialog.target.sender?.id;
          if (userId) {
            warnMutation.mutate({
              url: `/admin/chat-moderation/users/${userId}/warn`,
              method: 'post',
              values: { chatRoomId: room.id, reason },
            }, { onSuccess });
          }
        }
        break;
    }
  }, [actionDialog, room, reason, duration, editContent, deleteMutation, muteMutation, unmuteMutation, kickMutation, banMutation, unbanMutation, warnMutation, editMessageMutation, pinMutation, refetchMessages, refetchModerationActions, resetPagination, invalidate]);

  const isActionPending =
    deleteMutation.mutation.isPending ||
    muteMutation.mutation.isPending ||
    unmuteMutation.mutation.isPending ||
    kickMutation.mutation.isPending ||
    banMutation.mutation.isPending ||
    unbanMutation.mutation.isPending ||
    warnMutation.mutation.isPending ||
    editMessageMutation.mutation.isPending ||
    pinMutation.mutation.isPending;

  const isSendingMessage = sendMessageMutation.mutation.isPending;

  if (!room) {return null;}

  const currentActionType = actionDialog?.type;
  const currentActionNeedsReason = currentActionType ? actionNeedsReason(currentActionType) : false;
  const isConfirmDisabled = isActionPending || (currentActionNeedsReason && !reason.trim());
  const renderedMessages = [...messages].reverse().map((message) => {
    const senderParticipant = message.sender?.type === 'user'
      ? room.participants.find((participant) => (
          participant.type === 'user' && participant.id === message.sender?.id
        ))
      : undefined;

    return (
      <MessageItem
        key={message.id}
        message={message}
        onDelete={() => setActionDialog({ type: 'delete', target: message })}
        onWarn={() => setActionDialog({ type: 'warn', target: message })}
        onMute={senderParticipant
          ? () => setActionDialog({
              type: senderParticipant.isMuted ? 'unmute' : 'mute',
              target: senderParticipant,
            })
          : undefined}
        onPin={() => setActionDialog({
          type: message.isPinned ? 'unpin' : 'pin',
          target: message,
        })}
        isDeleting={deleteMutation.mutation.isPending}
        isPinned={message.isPinned}
        muteLabel={senderParticipant?.isMuted ? 'Размутить' : 'Замутить'}
        forceActionsOpen={forceMessageActionsOpen}
        menuPortalContainer={menuPortalContainer}
      />
    );
  });
  let chatPanelContent: ReactNode;
  if (activeChatTab === 'actions') {
    chatPanelContent = (
      <ModerationActionsList
        actions={moderationActions}
        isLoading={isLoadingModerationActions}
      />
    );
  } else if (isLoadingMessages) {
    chatPanelContent = (
      <div className="flex h-32 items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (messages.length === 0) {
    chatPanelContent = (
      <div className="flex h-32 flex-col items-center justify-center text-muted-foreground">
        <MessageSquare className="mb-2 h-8 w-8" />
        <p className="text-sm">Нет сообщений</p>
      </div>
    );
  } else {
    chatPanelContent = (
      <div className="space-y-2">
        {hasMoreMessages && (
          <div className="flex justify-center py-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadMoreMessages}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
              ) : null}
              Загрузить ещё
            </Button>
          </div>
        )}
        {renderedMessages}
      </div>
    );
  }

  return (
    <>
      <div className={className}>
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="flex items-center gap-2 text-base font-semibold">
                <MessageSquare className="h-4 w-4" />
                {getChatName(room)}
              </h3>
              <div className="mt-1 flex items-center gap-3 text-muted-foreground">
                <Badge variant={room.kind === ChatRoomKind.LIVESTREAM ? 'outline' : CHAT_ROOM_TYPE_COLORS[room.type]}>
                  {getChatTypeLabel(room)}
                </Badge>
                <span className="text-xs">
                  Создан: {formatDate(room.createdAt)}
                </span>
              </div>
            </div>
            {headerActions}
          </div>

          {canAdminSendMessage(room) ? (
            <div className="rounded-md border bg-muted/40 px-2 py-1.5">
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Send className="h-3 w-3" />
                {room.kind === ChatRoomKind.LIVESTREAM
                  ? 'Чат трансляции. Сообщения модератора видны участникам.'
                  : 'Чат поддержки. Вы можете отправлять ответы пользователю.'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border bg-muted/40 px-2 py-1.5">
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Eye className="h-3 w-3" />
                Вы наблюдаете в скрытом режиме. Участники не видят, что вы просматриваете чат.
              </p>
            </div>
          )}

          <div className={cn(
            showParticipants ? 'grid grid-cols-3 gap-3' : 'flex',
            contentHeightClassName,
          )}>
            <div className={showParticipants ? 'col-span-2' : 'min-w-0 flex-1'}>
              <Card className="h-full flex flex-col">
                <CardHeader className="flex-shrink-0 px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="flex items-center gap-1 text-sm font-medium">
                      <Button
                        type="button"
                        variant={activeChatTab === 'messages' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8"
                        onClick={() => setActiveChatTab('messages')}
                      >
                        Сообщения
                        <Badge variant="outline" className="ml-1 h-5 px-1.5 text-xs tabular-nums">
                          {room.messagesCount}
                        </Badge>
                      </Button>
                      <Button
                        type="button"
                        variant={activeChatTab === 'actions' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8"
                        onClick={() => setActiveChatTab('actions')}
                      >
                        Действия
                        <Badge variant="outline" className="ml-1 h-5 px-1.5 text-xs tabular-nums">
                          {moderationActions.length}
                        </Badge>
                      </Button>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (activeChatTab === 'messages') {
                          refetchMessages();
                        } else {
                          refetchModerationActions();
                        }
                      }}
                      disabled={activeChatTab === 'messages' ? isLoadingMessages : isLoadingModerationActions}
                    >
                      <RefreshCw className={cn(
                        'h-4 w-4',
                        (activeChatTab === 'messages' ? isLoadingMessages : isLoadingModerationActions) && 'animate-spin',
                      )} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-hidden px-3 pb-3">
                  <ScrollArea className="h-full pr-2">
                    {chatPanelContent}
                  </ScrollArea>
                </CardContent>
                {canAdminSendMessage(room) && (
                  <div className="border-t bg-muted/20 p-2">
                    <div className="flex gap-2">
                      <Textarea
                        ref={messageTextareaRef}
                        placeholder={room.kind === ChatRoomKind.LIVESTREAM ? 'Сообщение модератора в чат трансляции...' : 'Напишите ответ...'}
                        value={newMessage}
                        onChange={(e) => {
                          pendingTextareaSelectionRef.current = {
                            ownerDocument: e.currentTarget.ownerDocument,
                            selectionStart: e.currentTarget.selectionStart,
                            selectionEnd: e.currentTarget.selectionEnd,
                          };
                          setNewMessage(e.target.value);
                        }}
                        className="min-h-[48px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button
                        size="icon"
                        className="h-[48px] w-[48px]"
                        onClick={handleSendMessage}
                        disabled={isSendingMessage || !newMessage.trim()}
                      >
                        {isSendingMessage ? (
                          <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>

            {showParticipants && (
              <div className="col-span-1">
                <ParticipantsPanel
                  participants={room.participants}
                  onMute={(p) => setActionDialog({ type: 'mute', target: p })}
                  onKick={(p) => setActionDialog({ type: 'kick', target: p })}
                  onBan={(p) => setActionDialog({ type: 'ban', target: p })}
                  onUnmute={(p) => setActionDialog({ type: 'unmute', target: p })}
                  onUnban={(p) => setActionDialog({ type: 'unban', target: p })}
                  onWarn={(p) => setActionDialog({ type: 'warn', target: p })}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {currentActionType && menuPortalContainer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setActionDialog(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-action-dialog-title"
            aria-describedby="chat-action-dialog-description"
            className="relative grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg border bg-background p-6 text-foreground shadow-lg sm:max-w-lg"
          >
            <div className="flex flex-col gap-2 text-center sm:text-left">
              <h2 id="chat-action-dialog-title" className="text-lg leading-none font-semibold">
                {getActionDialogTitle(currentActionType)}
              </h2>
              <p id="chat-action-dialog-description" className="text-sm text-muted-foreground">
                {getActionDialogDescription(currentActionType)}
              </p>
            </div>
            <div className="space-y-4 py-4">
              {currentActionNeedsReason && (
                <div>
                  <Label htmlFor="chat-reason">Причина *</Label>
                  <Textarea
                    ref={reasonTextareaRef}
                    id="chat-reason"
                    placeholder="Укажите причину..."
                    value={reason}
                    onChange={(e) => {
                      pendingReasonSelectionRef.current = {
                        ownerDocument: e.currentTarget.ownerDocument,
                        selectionStart: e.currentTarget.selectionStart,
                        selectionEnd: e.currentTarget.selectionEnd,
                      };
                      setReason(e.target.value);
                    }}
                    className="mt-2"
                    rows={3}
                  />
                </div>
              )}
              {currentActionType === 'mute' && (
                <div>
                  <Label htmlFor="chat-duration">Длительность (минуты)</Label>
                  <select
                    id="chat-duration"
                    value={duration}
                    onChange={(event) => setDuration(event.currentTarget.value)}
                    className="border-input bg-background ring-offset-background focus-visible:ring-ring mt-2 flex h-9 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="15">15 минут</option>
                    <option value="60">1 час</option>
                    <option value="1440">24 часа</option>
                    <option value="10080">7 дней</option>
                    <option value="0">Бессрочно</option>
                  </select>
                </div>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setActionDialog(null)} disabled={isActionPending}>
                Отмена
              </Button>
              <Button
                variant={currentActionType === 'ban' || currentActionType === 'delete' ? 'destructive' : 'default'}
                onClick={handleAction}
                disabled={isConfirmDisabled}
              >
                {isActionPending ? 'Выполнение...' : 'Подтвердить'}
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 size-7 opacity-70 hover:opacity-100"
              onClick={() => setActionDialog(null)}
            >
              <span className="sr-only">Закрыть</span>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!actionDialog && !menuPortalContainer} onOpenChange={() => setActionDialog(null)}>
        {currentActionType && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{getActionDialogTitle(currentActionType)}</DialogTitle>
              <DialogDescription>{getActionDialogDescription(currentActionType)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {currentActionNeedsReason && (
                <div>
                  <Label htmlFor="chat-reason">Причина *</Label>
                  <Textarea
                    ref={reasonTextareaRef}
                    id="chat-reason"
                    placeholder="Укажите причину..."
                    value={reason}
                    onChange={(e) => {
                      pendingReasonSelectionRef.current = {
                        ownerDocument: e.currentTarget.ownerDocument,
                        selectionStart: e.currentTarget.selectionStart,
                        selectionEnd: e.currentTarget.selectionEnd,
                      };
                      setReason(e.target.value);
                    }}
                    className="mt-2"
                    rows={3}
                  />
                </div>
              )}
              {currentActionType === 'mute' && (
                <div>
                  <Label htmlFor="chat-duration">Длительность (минуты)</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger id="chat-duration" className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 минут</SelectItem>
                      <SelectItem value="60">1 час</SelectItem>
                      <SelectItem value="1440">24 часа</SelectItem>
                      <SelectItem value="10080">7 дней</SelectItem>
                      <SelectItem value="0">Бессрочно</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialog(null)} disabled={isActionPending}>
                Отмена
              </Button>
              <Button
                variant={currentActionType === 'ban' || currentActionType === 'delete' ? 'destructive' : 'default'}
                onClick={handleAction}
                disabled={isConfirmDisabled}
              >
                {isActionPending ? 'Выполнение...' : 'Подтвердить'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

export function ChatViewerSheet({
  room,
  open,
  onClose,
}: {
  room: ChatRoomWithStats | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[800px] sm:max-w-[800px]">
        <ChatModerationPanel room={room} />
      </SheetContent>
    </Sheet>
  );
}

function getActiveFiltersCount(typeFilter: string, isActiveFilter: string): number {
  let count = 0;
  if (typeFilter !== 'all') {count++;}
  if (isActiveFilter !== 'all') {count++;}
  return count;
}

export function ChatModerationTab() {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>({
    field: 'lastMessageAt',
    order: 'desc',
  });
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isActiveFilter, setIsActiveFilter] = useState<string>('all');
  const [selectedRoom, setSelectedRoom] = useState<ChatRoomWithStats | null>(null);
  const pageSize = 20;

  const activeFiltersCount = getActiveFiltersCount(typeFilter, isActiveFilter);

  const queryFilters = useMemo(() => {
    const result: Array<{ field: string; operator: 'eq'; value: string | boolean }> = [];
    if (typeFilter === 'livestream') {
      result.push({ field: 'kind', operator: 'eq', value: ChatRoomKind.LIVESTREAM });
    } else if (typeFilter !== 'all') {
      result.push({ field: 'type', operator: 'eq', value: typeFilter });
    }
    if (isActiveFilter !== 'all') {
      result.push({ field: 'isActive', operator: 'eq', value: isActiveFilter === 'true' });
    }
    return result;
  }, [typeFilter, isActiveFilter]);

  const listResult = useList<ChatRoomWithStats>({
    resource: 'chat-moderation/rooms',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const handleResetFilters = () => {
    setTypeFilter('all');
    setIsActiveFilter('all');
    setPage(1);
  };

  const bulkActions: BulkAction<ChatRoomWithStats>[] = [
    {
      label: 'Открыть чат',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          setSelectedRoom(items[0]);
        }
      },
      showOnlyWhenSelected: true,
    },
  ];

  const columns: Column<ChatRoomWithStats>[] = [
    {
      key: 'id',
      header: 'ID',
      width: '70px',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-xs text-muted-foreground">#{String(value)}</span>
      ),
    },
    {
      key: 'type',
      header: 'Тип',
      width: '140px',
      sortable: true,
      render: (_, item) => (
        <Badge variant={item.kind === ChatRoomKind.LIVESTREAM ? 'outline' : CHAT_ROOM_TYPE_COLORS[item.type]}>
          {getChatTypeLabel(item)}
        </Badge>
      ),
    },
    {
      key: 'name',
      header: 'Название / Участники',
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-primary" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-sm truncate">{getChatName(item)}</span>
            <span className="text-xs text-muted-foreground">
              {item.participantsCount} участников
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'messagesCount',
      header: 'Сообщений',
      width: '120px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Send className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'lastMessageAt',
      header: 'Последнее сообщение',
      width: '180px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(value as string | null)}
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '100px',
      render: (value) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Активен' : 'Неактивен'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (_, item) => (
        <Button variant="ghost" size="sm" onClick={() => setSelectedRoom(item)}>
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Фильтры
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="chat-type-filter">Тип чата</Label>
                <Select
                  value={typeFilter}
                  onValueChange={(v) => {
                    setTypeFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="chat-type-filter">
                    <SelectValue placeholder="Тип" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все типы</SelectItem>
                    <SelectItem value={ChatRoomType.DIRECT}>Личные чаты</SelectItem>
                    <SelectItem value={ChatRoomType.SUPPORT}>Поддержка</SelectItem>
                    <SelectItem value={ChatRoomType.GAME_LOBBY}>Игровые лобби</SelectItem>
                    <SelectItem value="livestream">Трансляции</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="chat-active-filter">Статус</Label>
                <Select
                  value={isActiveFilter}
                  onValueChange={(v) => {
                    setIsActiveFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="chat-active-filter">
                    <SelectValue placeholder="Статус" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Все</SelectItem>
                    <SelectItem value="true">Активные</SelectItem>
                    <SelectItem value="false">Неактивные</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={handleResetFilters}
                >
                  Сбросить фильтры
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(room) => setSelectedRoom(room)}
        emptyMessage="Нет чатов для модерации"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <ChatViewerSheet
        room={selectedRoom}
        open={!!selectedRoom}
        onClose={() => setSelectedRoom(null)}
      />
    </div>
  );
}
